// readingProgress remembers on this device how far the reader got in each
// story, so the home page can offer "Continue reading" and story lists can
// show progress bars. It is a convenience, not a record: storage may be
// unavailable or cleared, and then the app simply shows no progress.

const STORAGE_KEY = "readingProgress";
// The most recently read stories are kept; older entries are dropped.
const MAX_ENTRIES = 60;

// From this fraction on, a story counts as read to the end.
export const FINISHED_PROGRESS = 0.97;
// Below this fraction the reader has only glanced at the story.
const STARTED_PROGRESS = 0.02;

export interface ReadingEntry {
  storyId: string;
  // The learned language the story was read in; "Continue reading" only
  // offers stories of the current language pair.
  r: string;
  // Titles as shown while reading, for the "Continue reading" card.
  rTitle: string;
  lTitle: string | null;
  // Fraction of the story scrolled through, 0..1.
  progress: number;
  // Epoch milliseconds of the last update.
  updatedAt: number;
}

function isReadingEntry(value: unknown): value is ReadingEntry {
  const entry = value as ReadingEntry;
  return (
    !!entry &&
    typeof entry.storyId === "string" &&
    typeof entry.r === "string" &&
    typeof entry.rTitle === "string" &&
    (entry.lTitle === null || typeof entry.lTitle === "string") &&
    typeof entry.progress === "number" &&
    typeof entry.updatedAt === "number"
  );
}

// loadReadingEntries returns the remembered stories, most recent first.
// Corrupt or foreign storage content is ignored entry by entry.
export function loadReadingEntries(): ReadingEntry[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    if (!Array.isArray(raw)) return [];
    return raw.filter(isReadingEntry).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch (err) {
    console.error("Failed to load reading progress", err);
    return [];
  }
}

// recordReadingProgress stores the reader's position in a story. Progress
// never moves backwards past the end: re-reading a finished story from the
// top keeps it finished.
export function recordReadingProgress(entry: Omit<ReadingEntry, "updatedAt">) {
  const entries = loadReadingEntries();
  const previous = entries.find((e) => e.storyId === entry.storyId);
  const progress =
    previous && previous.progress >= FINISHED_PROGRESS
      ? Math.max(previous.progress, entry.progress)
      : entry.progress;
  const updated: ReadingEntry[] = [
    { ...entry, progress, updatedAt: Date.now() },
    ...entries.filter((e) => e.storyId !== entry.storyId),
  ].slice(0, MAX_ENTRIES);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error("Failed to store reading progress", err);
  }
}

// Generated stories can only be opened by their author.
function isGeneratedStoryId(storyId: string): boolean {
  return storyId.startsWith("g_");
}

// forgetGeneratedStoriesProgress drops the entries of generated stories, so
// that whoever uses the device after their author signed out isn't offered
// them (with their titles) to continue reading.
export function forgetGeneratedStoriesProgress() {
  const entries = loadReadingEntries();
  const keptEntries = entries.filter((e) => !isGeneratedStoryId(e.storyId));
  if (keptEntries.length === entries.length) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keptEntries));
  } catch (err) {
    console.error("Failed to forget generated stories' reading progress", err);
  }
}

export function readingProgressByStoryId(
  entries: ReadingEntry[],
): Map<string, number> {
  return new Map(entries.map((e) => [e.storyId, e.progress]));
}

// isInProgress says whether a story was started but not read to the end.
export function isInProgress(progress: number | undefined): boolean {
  return (
    progress !== undefined &&
    progress >= STARTED_PROGRESS &&
    progress < FINISHED_PROGRESS
  );
}
